import { MessageCircle } from "lucide-react";
import { useState } from "react";

import Comment from "./Comment";
import CommentInput from "./CommentInput";
import useCommentActions from "./hooks/useCommentActions";

import { useEvent } from "@/components/Changelog/ChangelogItem/changelog-item-context";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import useActiveWorkspace from "@/hooks/useActiveWorkspace";
import { useGetTopLevelComments } from "@/queries/changelog";

const CommentSection = () => {
  const event = useEvent();
  const activeWorkspace = useActiveWorkspace();
  const workspaceId = activeWorkspace?.id ?? 0;
  const changelogId = event.id;

  const { data, isLoading } = useGetTopLevelComments({
    workspaceId,
    changelogId,
  });

  const {
    handleReply,
    handleAddComment,
    handleUpdateComment,
    handleDeleteComment,
    isCreating,
    isCommentPending,
    isCommentOptimistic,
  } = useCommentActions({
    workspaceId,
    changelogId,
  });

  const comments = data?.items ?? [];
  const hasComments = comments.length > 0;

  const [startCommenting, setStartCommenting] = useState(false);
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);

  const handleSubmitComment = async (text: string) => {
    setIsSubmittingComment(true);
    try {
      await handleAddComment(text);
      setStartCommenting(false);
    } finally {
      setIsSubmittingComment(false);
    }
  };

  if (isLoading) {
    return (
      <div className="mt-4 space-y-3 border-t border-gray-100 pt-4">
        <div className="flex items-center gap-2">
          <MessageCircle className="h-4 w-4 text-muted-foreground" />
          <Skeleton className="h-4 w-24" />
        </div>
        <Skeleton className="h-16 w-full" />
        <Skeleton className="ml-8 h-12 w-[calc(100%-2rem)]" />
      </div>
    );
  }

  return (
    <div className="mt-4 border-t border-gray-100 pt-4">
      {hasComments && (
        <>
          {/* Header */}
          <div className="mb-4 flex items-center gap-2">
            <MessageCircle className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-medium text-foreground">
              {comments.length} {comments.length === 1 ? "comment" : "comments"}
            </h3>
          </div>

          {/* Comments List */}
          <div className="mb-4 space-y-1">
            {comments.map(comment => (
              <Comment
                key={comment.id}
                comment={comment}
                onReply={handleReply}
                onDelete={handleDeleteComment}
                onUpdate={handleUpdateComment}
                isCommentPending={isCommentPending}
                isCommentOptimistic={isCommentOptimistic}
              />
            ))}
          </div>
        </>
      )}
      {!startCommenting ? (
        <Button
          variant="ghost"
          size="sm"
          className="h-8 gap-2 text-muted-foreground hover:text-foreground"
          onClick={() => {
            // This will be handled by showing the input below
            setStartCommenting(true);
          }}
        >
          <MessageCircle className="h-4 w-4" />
          <span className="text-sm">Add a comment</span>
        </Button>
      ) : null}

      {/* Add Comment Section */}
      {startCommenting && (
        <div id={`comment-input-${changelogId}`}>
          <CommentInput
            onSubmit={handleSubmitComment}
            placeholder="Add a comment..."
            onCancel={() => {
              setStartCommenting(false);
            }}
            submitButtonText="Comment"
            showActionsOnMount={false}
            autoFocus // eslint-disable-line jsx-a11y/no-autofocus
            isSubmitting={isSubmittingComment || isCreating}
          />
        </div>
      )}
    </div>
  );
};

export default CommentSection;
